import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Flex } from "@radix-ui/themes";
import { Textarea } from "@geist-ui/core";

import { Dialog } from "@/components/shared/dialog";
import { useGlobalInstructionApi } from "@/v2/entities/global-instruction/GlobalInstruction.api";
import { useSimulator } from "../../context/SimulatorWrapper";

import styles from "../briefDialog/styles.module.scss";

export const GlobalInstructionDialog = ({
  open,
  openChange,
}: {
  open: boolean;
  openChange: (value: boolean) => void;
}) => {
  const [text, setText] = useState("");
  const [originalText, setOriginalText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { currentRelease, refreshReleaseData } = useSimulator();
  const globalInstructionApi = useGlobalInstructionApi();

  const currentGlobalInstruction = useMemo(() => {
    if (!currentRelease?.globalInstructions) return null;
    return currentRelease.globalInstructions[0] || null;
  }, [currentRelease?.globalInstructions]);

  useEffect(() => {
    if (open && currentRelease) {
      if (currentGlobalInstruction) {
        setText(currentGlobalInstruction.content.trim() || "");
        setOriginalText(currentGlobalInstruction.content);
      } else {
        setText("");
        setOriginalText("");
      }
    }
  }, [open, currentGlobalInstruction, currentRelease]);

  const hasChanges = useMemo(() => {
    return text.trim() !== originalText.trim();
  }, [text, originalText]);

  const handleSave = useCallback(async () => {
    if (!currentRelease?.id) return;

    setIsLoading(true);
    try {
      const content = text.trim() || " ";

      if (currentGlobalInstruction) {
        await globalInstructionApi.updateGlobalInstruction(
          currentGlobalInstruction.id,
          { content }
        );
      } else {
        await globalInstructionApi.createGlobalInstruction({
          content,
          releaseId: currentRelease.id,
        });
      }
      setOriginalText(content);
      await refreshReleaseData();
      openChange(false);
    } catch (error) {
      console.error("failed to save global instruction:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    text,
    currentRelease?.id,
    currentGlobalInstruction,
    globalInstructionApi,
    refreshReleaseData,
    openChange,
  ]);

  return (
    <Dialog open={open} onOpenChange={openChange} loading={isLoading}>
      <div className={styles.briefDialog}>
        <Dialog.Content size="large">
          <Dialog.Title>
            Global Instructions
            <Dialog.Close />
          </Dialog.Title>
          <div className={styles.briefContent}>
            <Textarea
              width="100%"
              height="100%"
              placeholder="Enter global instructions that apply to all topics..."
              onChange={(e) => setText(e.target.value)}
              value={text}
              dir="auto"
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
            <Flex gap="3" justify="end" mt="24px" mb="24px" px="24px">
              <Button
                variant="ghost"
                color="gray"
                onClick={() => openChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="gray"
                highContrast
                onClick={handleSave}
                disabled={!hasChanges || isLoading}
              >
                Save
              </Button>
            </Flex>
          </div>
        </Dialog.Content>
      </div>
    </Dialog>
  );
};
